/**
 * SATIntroGate — Mount-Wrapper.
 *
 * Verantwortlich für:
 *   – Feature-Flag & Route-Gate (nur "/", Session-Guard)
 *   – Scroll-Lock während der Intro-Sequenz
 *   – Prevent-Duplicates (rendert Intro immer nur einmal pro Session)
 *   – SOFORTIGER dunkler Backdrop, damit die Homepage nicht aufblitzt,
 *     während der Intro-Chunk lazy geladen wird (Fix „weisse Seite").
 *   – Nach Intro-Ende: kurzer `GoldenAfterglow` für nahtlosen Homepage-Übergang
 */
import React, { lazy, Suspense, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { PALETTE, shouldPlayIntro } from "./introConfig";

const SATTransformationIntro = lazy(() => import("./SATTransformationIntro"));
const GoldenAfterglow = lazy(() => import("./GoldenAfterglow"));

/**
 * Fullscreen-Dunkel-Backdrop, der SOFORT gerendert wird sobald das Intro
 * spielen soll — sowohl als Suspense-Fallback wie auch beim allerersten
 * Paint. Verhindert den weissen/hellen Aufblitz der Homepage vor dem Intro.
 */
const IntroBackdrop = () => (
  <div
    aria-hidden="true"
    className="fixed inset-0 z-[9999] pointer-events-none"
    data-testid="cinematic-intro-backdrop"
    style={{
      background: `radial-gradient(ellipse at 50% 55%, ${PALETTE.BG_MID} 0%, ${PALETTE.BG_DEEP} 70%, #01030a 100%)`,
    }}
  />
);

const SATIntroGate = () => {
  const { pathname } = useLocation();
  // Initial-Phase SYNCHRON bestimmen — kein useEffect-Delay mehr.
  // Damit steht der dunkle Backdrop schon beim ersten Paint.
  const [phase, setPhase] = useState(() =>
    shouldPlayIntro(pathname) ? "intro" : "idle",
  );

  // Scroll-Lock nur während Intro (nicht während Afterglow)
  useEffect(() => {
    if (phase !== "intro") return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [phase]);

  if (phase === "idle" || phase === "done") return null;

  return (
    <>
      {phase === "intro" && <IntroBackdrop />}
      <Suspense fallback={phase === "intro" ? <IntroBackdrop /> : null}>
        {phase === "intro" && (
          <SATTransformationIntro onFinish={() => setPhase("afterglow")} />
        )}
        {phase === "afterglow" && <GoldenAfterglow />}
      </Suspense>
    </>
  );
};

export default SATIntroGate;
